import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FeatureHeader from './FeatureHeader';
import Tile from './Tile';
import SharedBuffySprite from '../buffy/BuffySprite';
import { SLOW, useMedia, useReveal } from './useReveal';
import './features.css';

/* F4 "Assign practice. Track progress." (PAGE STRUCTURE v3, 2026-09-29):
   a boxed, looping "video" in the same product chat look as F3 Ask Buffy
   (the soft #f3f5ef pill for the teacher's words, in ink; Buffy's plain
   reply; one white card; the 64px composer), with Buffy standing beside the
   box on its bottom line the whole loop.

   The loop: the teacher's request types into the composer and sends, Buffy
   thinks, answers, and shows the draft; its Assign button presses to
   Assigned. The composer never scrolls sideways: as typing starts it grows
   to the lines the whole request needs (the untyped tail is reserved
   invisibly, so no word jumps lines) and shrinks back on send; the white
   card behind it plays that as a FLIP (scaleY, then none), so the text is
   never scaled and everything is crisp at rest. Then the chat fades and a progress dashboard rises into the
   same box (the product's teacher-dashboard look): Advanced Math over the
   last 4 weeks, the class's average level rising 2.8 to 3.6 as a line
   chart, and three students with their level change. Then it clears and
   loops.

   Like F3, the whole box is a pure function of one clock `t` (frameAt), so
   pausing off screen is stopping the clock; ?settled=1 and reduced motion
   draw the final frame as a static transcript with the dashboard under it
   (the box grows to fit). The chat classes (cxs-chat*, cxs-msg*,
   cxs-ab-*) are F3's, so the two boxes can never drift apart.

   Buffy (the text is on the RIGHT here, so he stands on the box's right,
   mirrored to face it; legal, his base pose is symmetrical): think while
   Buffy is thinking, celebrate on Assigned, point at the dashboard (from
   the top edge, under 900px, a point would aim off the box, so he
   encourages there). Students are initials and names only.

   Sample data: SAMPLE DATA CANON (DECISIONS.md). F2 shows the class's
   Advanced Math average at 2.8 and F3 finds the same 3 students (AL, SN,
   DP) stuck on factoring; this assigns them practice and shows 2.8 -> 3.6.
   The Friday due date is the product's default. */

const Q = 'Assign practice on factoring for those 3 students, due Friday.';
const PLACEHOLDER = 'Ask Buffy about your class…';
// one sentence per line (the second never leaves "say so." alone)
const REPLY_LINES = ['Here’s a set at each student’s level.', 'Nothing goes out until you say so.'];
const REPLY = REPLY_LINES.join(' ');
const WEEKS = [2.8, 3.1, 3.3, 3.6];
const Y_MIN = 2;
const Y_MAX = 4;
const STUDENTS = [
  { ini: 'AL', name: 'Alex L.', from: 2, to: 3, fill: '#C8F3DD' },
  { ini: 'SN', name: 'Sam N.', from: 2, to: 3, fill: '#FFD6DC' },
  { ini: 'DP', name: 'Dana P.', from: 3, to: 4, fill: '#DCD5FF' },
];

// base ms; the clock runs SLOW (1.35x) slower than these numbers
const GROW = 320; // the composer opens to its lines before the first key
const TYPE = 30;
const SEND = 320;
const THINK = 900;
const CARD = 520;
const ASSIGN = 1500;
const HOLD = 1700;
const CLEAR = 650;
const DASH = 5400;
const OUT = 700;

// `q`: the teacher's request (the schools page passes its own; default Q)
function buildTimeline(q = Q) {
  const tStart = 500;
  const tSent = tStart + GROW + q.length * TYPE + SEND;
  const tReply = tSent + THINK;
  const tCard = tReply + CARD;
  const tAssign = tCard + ASSIGN;
  const tClear = tAssign + HOLD;
  const tDash = tClear + CLEAR;
  const tOut = tDash + DASH;
  return { q, tStart, tSent, tReply, tCard, tAssign, tClear, tDash, tOut, loop: tOut + OUT };
}

function frameAt(T, t) {
  const items = [];
  let typed = '';
  let typing = false;
  let caret = false;
  let cue = null;
  if (t >= T.tStart && t < T.tSent) {
    typing = true;
    caret = t >= T.tStart + GROW;
    typed = T.q.slice(0, Math.max(0, Math.min(T.q.length, Math.floor((t - T.tStart - GROW) / TYPE))));
  }
  if (t >= T.tSent && t < T.tDash) {
    items.push({ id: 'q', kind: 'you' });
    if (t < T.tReply) items.push({ id: 't', kind: 'thinking' });
    else items.push({ id: 'r', kind: 'buffy' });
    if (t >= T.tCard) items.push({ id: 'c', kind: 'draft' });
  }
  if (t >= T.tSent) cue = { clip: 'think', id: 0 };
  if (t >= T.tAssign + 150) cue = { clip: 'celebrate', id: 1 };
  if (t >= T.tDash + 300) cue = { clip: 'point', id: 2 };
  const dash = t >= T.tOut ? 'out' : t >= T.tDash ? 'in' : 'off';
  return { items, typed, typing, caret, assigned: t >= T.tAssign, clearing: t >= T.tClear, dash, cue };
}

// the still frame: the whole exchange, the draft assigned, the dashboard
const STILL = {
  items: [{ id: 'q', kind: 'you' }, { id: 'r', kind: 'buffy' }, { id: 'c', kind: 'draft' }],
  typed: '',
  typing: false,
  caret: false,
  assigned: true,
  clearing: false,
  dash: 'in',
  cue: null,
};

function frameKey(f) {
  return `${f.items.map((x) => x.id).join(',')}|${f.typed}|${f.typing}|${f.caret}|${f.assigned}|${f.clearing}|${f.dash}|${f.cue ? f.cue.id : '-'}`;
}

function ArrowUp() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path d="M8 13V3.5M3.8 7.5L8 3.3l4.2 4.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Draft({ assigned }) {
  return (
    <div className="cx-surface cxs-chat-card cxs-chat-draft">
      <span className="cxs-chat-draft-copy">
        <span className="cxs-chat-draft-t">Factoring practice</span>
        <span className="cx-t-label cxs-muted">{assigned ? '3 students · Due Fri' : 'Draft · 3 students · Due Fri'}</span>
      </span>
      <span key={assigned ? 'done' : 'draft'} className={`cx-action${assigned ? ' is-done cxs-chat-pressed' : ''}`}>
        {assigned ? 'Assigned' : 'Assign'}
      </span>
    </div>
  );
}

function Item({ it, assigned, q }) {
  if (it.kind === 'you') return <div className="cxs-msg cxs-msg-you">{q}</div>;
  if (it.kind === 'thinking') {
    return (
      <div className="cxs-msg cxs-msg-think">
        <span className="cxs-dots" aria-hidden="true"><i /><i /><i /></span>
        Buffy is thinking
      </div>
    );
  }
  if (it.kind === 'buffy') {
    return <p className="cxs-msg cxs-msg-buffy cxf-reply">{REPLY_LINES.map((l) => <span key={l}>{l}</span>)}</p>;
  }
  return <div className="cxs-msg cxs-msg-card"><Draft assigned={assigned} /></div>;
}

/* The class's average level by week, drawn in px from the measured width
   (like F1's connectors) so the dots stay round and the line can draw with
   pathLength + stroke-dashoffset. */
function LevelChart() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const set = () => {
      setW(el.clientWidth);
      setH(el.clientHeight);
    };
    set();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const padX = 12;
  const padY = 8;
  const gut = 18; // the y labels' lane
  const x0 = gut + padX;
  const x1 = Math.max(x0 + 1, w - padX);
  const y = (v) => Math.round(padY + (1 - (v - Y_MIN) / (Y_MAX - Y_MIN)) * (h - padY * 2)) + 0.5;
  const pts = WEEKS.map((v, i) => [Math.round(x0 + (i * (x1 - x0)) / (WEEKS.length - 1)), y(v)]);
  const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px} ${py}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0]} ${h - padY} L${pts[0][0]} ${h - padY} Z`;
  return (
    <div className="cxf-chart">
      <div ref={ref} className="cxf-chart-plot">
        {[Y_MAX, 3, Y_MIN].map((v) => (
          <span key={v} className="cxf-chart-grid" style={{ top: h ? y(v) - 0.5 : 0 }}>
            <span className="cxf-chart-y">{v}</span>
          </span>
        ))}
        {w && h ? (
          <svg className="cxf-chart-svg" width={w} height={h} aria-hidden="true" focusable="false">
            <path className="cxf-chart-area cxf-a-fade" d={area} style={{ '--d': '1300ms' }} />
            <path className="cxf-chart-line cxf-a-draw" d={line} pathLength="1" style={{ '--d': '500ms' }} />
            {pts.map(([px, py], i) => (
              <circle
                key={px}
                className={`cxf-chart-dot cxf-a-pop${i === pts.length - 1 ? ' is-now' : ''}`}
                cx={px}
                cy={py}
                r={i === pts.length - 1 ? 5 : 3.5}
                style={{ '--d': `${560 + i * 220}ms` }}
              />
            ))}
          </svg>
        ) : null}
      </div>
      <div className="cxf-chart-x" style={{ paddingLeft: gut }}>
        {WEEKS.map((v, i) => <span key={`w${v}`}>{`Week ${i + 1}`}</span>)}
      </div>
    </div>
  );
}

function Dashboard({ out }) {
  return (
    <div className={`cx-surface cxf-dash${out ? ' is-out' : ''}`}>
      <div className="cxf-dash-head">
        <span className="cxf-dash-title">Advanced Math</span>
        <span className="cxf-dash-meta">Last 4 weeks</span>
      </div>
      <div className="cxf-dash-body">
        <div className="cxf-dash-left">
          <div className="cxf-dash-k">Class average level <span className="cxf-sample-label">Sample data</span></div>
          <div className="cxf-dash-big cxf-a-fade" style={{ '--d': '1300ms' }}>
            <span className="cxf-dash-num">3.6</span>
            <span className="cxf-dash-from">up from 2.8</span>
          </div>
          <LevelChart />
        </div>
        <div className="cxf-dash-right">
          <span className="cxf-dash-chip cxf-a-pop" style={{ '--d': '2300ms' }}>
            Moved up this week
            <b>3</b>
          </span>
          <ul className="cxf-dash-st">
            {STUDENTS.map((s, i) => (
              <li key={s.ini} className="cxf-st cxf-a-fade" style={{ '--d': `${900 + i * 140}ms` }}>
                <span className="cxs-ini" style={{ background: s.fill }}>{s.ini}</span>
                <span className="cxf-st-name">{s.name}</span>
                <span className="cxf-st-lv">
                  <span className="cxs-muted">{`Level ${s.from}`}</span>
                  <span className="cxf-st-arrow" aria-hidden="true">{'→'}</span>
                  <span className="cxf-st-to cxf-a-pop" style={{ '--d': `${1500 + i * 180}ms` }}>{s.to}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* F3's cue-driven Buffy (AskBuffy.jsx ChatBuffy), plus `mirror`: gestures on
   the chat's cues, idle breathing between them. `side` = beside the box, so
   a point aims into it; otherwise 'point' plays 'encourage'. */
function ChatBuffy({ size, cue, paused, still, side, mirror, className }) {
  const [clip, setClip] = useState('idle-breathe');
  const [run, setRun] = useState(0);
  const pending = useRef(null);
  const cueId = cue ? `${cue.id}` : null;
  useEffect(() => {
    if (!cue || still) return;
    const next = cue.clip === 'point' && !side ? 'encourage' : cue.clip;
    if (clip === 'idle-breathe') {
      setClip(next);
      setRun((r) => r + 1);
    } else {
      pending.current = next;
    }
    // the cue id is the trigger; clip is read, not a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueId, still]);
  const onEnd = () => {
    const next = pending.current || 'idle-breathe';
    pending.current = null;
    setClip(next);
    setRun((r) => r + 1);
  };
  const idle = clip === 'idle-breathe';
  return (
    <span className={`cxs-buffy ${className}`} style={{ width: size, height: size }} aria-hidden="true">
      <SharedBuffySprite
        clip={still ? 'rest' : clip}
        scale={Math.round(size / 64)}
        mirror={mirror}
        paused={paused || still}
        loop={still ? false : idle}
        run={run}
        trim={!idle}
        onEnd={still || idle ? undefined : onEnd}
      />
    </span>
  );
}

function useChatClock(T, playing, still, paused) {
  const [frame, setFrame] = useState(() => (still ? STILL : frameAt(T, 0)));
  const clock = useRef(0);
  // the pause button inside a fade (the chat clearing, or the dashboard
  // fading out) holds the full frame just before it; play resumes there
  useEffect(() => {
    if (!paused || still) return;
    const t = clock.current;
    let snap = null;
    if (t >= T.tClear && t < T.tDash) snap = T.tClear - 1;
    else if (t >= T.tOut) snap = T.tOut - 1;
    if (snap == null) return;
    clock.current = snap;
    setFrame(frameAt(T, snap));
  }, [T, paused, still]);
  useEffect(() => {
    if (still) {
      setFrame(STILL);
      return undefined;
    }
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let key = '';
    const tick = (now) => {
      clock.current += (now - last) / SLOW;
      last = now;
      if (clock.current >= T.loop) clock.current = 0;
      const f = frameAt(T, clock.current);
      const k = frameKey(f);
      if (k !== key) {
        key = k;
        setFrame(f);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [T, playing, still]);
  return frame;
}

/* New messages push the log up (F3's FLIP slide, transform only). */
function useChatScroll(ref, itemsKey, still) {
  const prevH = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || still) return;
    const h = el.offsetHeight;
    const delta = h - prevH.current;
    prevH.current = h;
    if (!delta || h === 0) return;
    el.style.transition = 'none';
    el.style.transform = `translate3d(0, ${delta}px, 0)`;
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.style.transition = `transform ${Math.round(460 * SLOW)}ms cubic-bezier(.2, .8, .2, 1)`;
    el.style.transform = 'translate3d(0, 0, 0)';
  }, [ref, itemsKey, still]);
}

/* The composer grows to the request's lines while it types and shrinks back
   on send. Its layout height changes at once; the white card behind the
   text plays the change (FLIP: scaleY from the old height, anchored at the
   bottom, back to none), so the text itself is never scaled. */
function useComposerFlip(boxRef, bgRef, typing, still) {
  const prevH = useRef(0);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const bg = bgRef.current;
    if (!box || !bg) return;
    const h = box.offsetHeight;
    const from = prevH.current;
    prevH.current = h;
    if (still || !from || from === h) return;
    bg.style.transition = 'none';
    bg.style.transform = `scaleY(${from / h})`;
    // eslint-disable-next-line no-unused-expressions
    bg.offsetHeight;
    bg.style.transition = `transform ${Math.round(280 * SLOW)}ms cubic-bezier(.2, .8, .2, 1)`;
    bg.style.transform = '';
  }, [boxRef, bgRef, typing, still]);
}

/* The scene alone (schools page 2026-10-01: its "For teachers and
   students" tab shows it without this section's heading). `question`
   replaces the teacher's request (the teacher page's default follows F3,
   which found "those 3 students"). */
export function AssignTrackScene({ question = Q }) {
  const { ref, reveal, live, still } = useReveal({ amount: 0.4 });
  // 900px+: Buffy stands beside the box on its bottom line, at its right
  // (the text side); narrower, on the box's top edge, like F3
  const side = useMedia('(min-width: 900px)');
  const big = useMedia('(min-width: 1280px)');
  const size = side && big ? 192 : 128;

  const T = useMemo(() => buildTimeline(question), [question]);
  const playing = reveal === 'play' && live;
  const f = useChatClock(T, playing, still, false);
  const logRef = useRef(null);
  useChatScroll(logRef, f.items.map((x) => x.id).join(','), still);
  const compRef = useRef(null);
  const compBgRef = useRef(null);
  useComposerFlip(compRef, compBgRef, f.typing, still);

  return (
    <div ref={ref} className="cxf-track-stage" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
      <Tile look="site" canvas="white" className={`cxs-ask-tile cxf-track-tile${still ? ' is-still' : ''}`} aria-hidden="true">
        <div className="cxs-chat">
          <div className="cxf-screen">
            <div className={`cxs-chat-log${f.clearing ? ' is-clearing' : ''}`}>
              <div ref={logRef} className="cxs-chat-inner">
                {f.items.map((it) => <Item key={it.id} it={it} assigned={f.assigned} q={T.q} />)}
                {still ? <div className="cxs-msg cxf-dash-still"><Dashboard /></div> : null}
              </div>
            </div>
            {!still && f.dash !== 'off' ? <Dashboard out={f.dash === 'out'} /> : null}
          </div>
          <div ref={compRef} className="cxs-ab-composer cxf-composer">
            <span ref={compBgRef} className="cx-surface cxf-comp-bg" />
            <span className={`cxs-ab-input cxf-input${f.typing ? '' : ' is-empty'}`}>
              {f.typing ? (
                <>
                  {f.typed}
                  {f.caret ? <span className="cxs-caret" aria-hidden="true" /> : null}
                  <span className="cxs-ghost" aria-hidden="true">{T.q.slice(f.typed.length)}</span>
                </>
              ) : PLACEHOLDER}
            </span>
            <span className={`cxs-ab-send${f.typing && f.typed ? ' is-on' : ''}`}><ArrowUp /></span>
          </div>
        </div>
      </Tile>
      {/* the loop, for screen readers */}
      <ol className="cxs-sr">
        <li>{`Teacher: ${T.q}`}</li>
        <li>{`Buffy: ${REPLY} Draft: Factoring practice, 3 students, due Friday. The teacher clicks Assign.`}</li>
        <li>
          Dashboard, Advanced Math, last 4 weeks: the class average level rose from 2.8 to 3.6. 3 students moved up this week:
          Alex L. and Sam N. from level 2 to 3, Dana P. from level 3 to 4.
        </li>
      </ol>
      <ChatBuffy
        className={`cxf-track-buffy ${side ? 'is-side' : 'is-top'} is-${size}`}
        size={size}
        cue={f.cue}
        paused={!live}
        still={still}
        side={side}
        mirror={side}
      />
    </div>
  );
}

/* `header` (schools preview, 2026-09-30): optional FeatureHeader overrides
   (title, sub, side, id, as, labelled, after, className added to ours) so the
   schools page can reuse this scene under its own words. With no props the
   teacher page renders exactly as before. */
export default function AssignTrackSection({ header = {} }) {
  return (
    <FeatureHeader
      n={4}
      title={(
        <>
          <span>Assign practice.</span>
          {' '}
          <span className="cxs-h2-tail">Track progress.</span>
        </>
      )}
      sub="Ask Buffy to assign practice at each student’s level, then watch their levels move up over time."
      side="right"
      id="cxs-f4"
      {...header}
      className={`cxf-f4${header.className ? ` ${header.className}` : ''}`}
    >
      <AssignTrackScene />
    </FeatureHeader>
  );
}
